// src/pages/CotizacionForm.js
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { crearCotizacionCompleta } from "../services/cotizacionService";
import { buscarClientePorClave } from "../services/clienteService";
import { buscarArticuloPorClave } from "../services/articuloService";
import { buscarProductosSyscom } from "../services/syscomService";
import Toast, { useToast } from "../components/Toast";

/* ===== Helpers ===== */
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const fmtMX = (n) =>
  round2(n).toLocaleString("es-MX", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const calcPrecioVenta = (precioBase, aum) => {
  const base = num(precioBase);
  const pAum = num(aum);
  if (pAum > 0 && pAum < 100) return round2(base / (1 - pAum / 100));
  return round2(base);
};
const calcImportes = ({ precioBase, aum = 0, desc = 0, qty = 1 }) => {
  const cantidad = num(qty);
  const pVenta = calcPrecioVenta(precioBase, aum);
  const pUnitDesc =
    num(desc) > 0 ? round2(pVenta * (1 - num(desc) / 100)) : pVenta;
  const impDesc = round2(cantidad * pUnitDesc);
  return { pVenta, pUnitDesc, impDesc };
};

const EMPTY_ITEM = {
  articulo: "",
  descripcion: "",
  unidadMedida: "PZA",
  claveUnidad: "",
  codSat: "",
  cantidad: 1,
  precioCompra: 0,
  porcDescto: 0,
  porcIncremento: 0,
  codigoFabricante: "",
};
const ensureItem = (x) => ({ ...EMPTY_ITEM, ...(x || {}) });

/* ===== Input de celda de tabla — definido FUERA para evitar pérdida de foco ===== */
const CellInput = ({
  value,
  onChange,
  onBlur,
  onKeyDown,
  placeholder,
  type = "text",
  align = "left",
}) => (
  <input
    type={type}
    value={value ?? ""}
    onChange={onChange}
    onBlur={onBlur}
    onKeyDown={onKeyDown}
    placeholder={placeholder}
    step={type === "number" ? "0.0001" : undefined}
    style={{
      width: "100%",
      padding: "5px 7px",
      border: "1px solid var(--clr-border)",
      borderRadius: "var(--radius-sm)",
      fontSize: "var(--fs-sm)",
      fontFamily: "var(--font-sans)",
      background: "#fff",
      outline: "none",
      textAlign: align,
      boxSizing: "border-box",
    }}
    onFocus={(e) => {
      e.target.style.borderColor = "var(--clr-primary)";
      e.target.style.boxShadow = "var(--shadow-focus)";
    }}
    onBlurCapture={(e) => {
      e.target.style.borderColor = "var(--clr-border)";
      e.target.style.boxShadow = "none";
    }}
  />
);

export default function CotizacionForm() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    cliente: "",
    nombreCliente: "",
    fecha: new Date().toISOString().slice(0, 10),
    moneda: "P",
    tipoCambio: 0,
    observaciones: "",
    notasAdicionales: "",
    porcDsctoGlobal: 0,
    incrementoGlobal: 0,
  });

  const [clienteMsg, setClienteMsg] = useState("");
  const [clienteLoading, setClienteLoading] = useState(false);
  const [rowMsgs, setRowMsgs] = useState({});
  const [items, setItems] = useState([{ ...EMPTY_ITEM }]);
  const [saving, setSaving] = useState(false);
  const { toasts, closeToast, error: toastError, warning: toastWarning } = useToast();

  // Buscador de Syscom (modal por renglón)
  const [syscomModalIdx, setSyscomModalIdx] = useState(null);
  const [syscomQuery, setSyscomQuery] = useState("");
  const [syscomResultados, setSyscomResultados] = useState([]);
  const [syscomLoading, setSyscomLoading] = useState(false);
  const [syscomError, setSyscomError] = useState("");
  const [syscomBuscado, setSyscomBuscado] = useState(false);

  const setRowMsg = (idx, msg) =>
    setRowMsgs((m) => ({ ...m, [idx]: msg || "" }));

  const addRow = () => setItems((arr) => [...arr, { ...EMPTY_ITEM }]);
  const rmRow = (idx) => setItems((arr) => arr.filter((_, i) => i !== idx));

  const onChangeHeader = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({
      ...f,
      [name]: ["porcDsctoGlobal", "incrementoGlobal", "tipoCambio"].includes(
        name,
      )
        ? Number(value)
        : value,
    }));
  };

  const onChangeItem = (idx, name, value) => {
    setItems((arr) => {
      const next = arr.slice();
      next[idx] = ensureItem(next[idx]);
      if (
        ["cantidad", "precioCompra", "porcDescto", "porcIncremento"].includes(
          name,
        )
      ) {
        next[idx][name] = Number(value);
      } else {
        next[idx][name] = value ?? "";
      }
      return next;
    });
  };

  const onClienteBlur = async () => {
    const clave = form.cliente?.trim();
    if (!clave) {
      setClienteMsg("");
      setForm((f) => ({ ...f, nombreCliente: "" }));
      return;
    }
    setClienteMsg("");
    setClienteLoading(true);
    try {
      const empresaId = Number(localStorage.getItem("empresaId") || 1);
      const cli = await buscarClientePorClave(empresaId, clave);
      if (!cli) {
        setForm((f) => ({ ...f, nombreCliente: "" }));
        setClienteMsg("El cliente no existe.");
        return;
      }
      setForm((f) => ({
        ...f,
        nombreCliente: cli.nombre ?? cli.razonSocial ?? cli.nombreCliente ?? "",
      }));
    } catch {
      setForm((f) => ({ ...f, nombreCliente: "" }));
      setClienteMsg("No se pudo validar el cliente.");
    } finally {
      setClienteLoading(false);
    }
  };

  const lookupArticulo = async (idx) => {
    const clave = (ensureItem(items[idx]).articulo || "").trim();
    if (!clave) {
      setRowMsg(idx, "");
      return;
    }
    try {
      setRowMsg(idx, "Buscando...");
      const empresaId = Number(localStorage.getItem("empresaId") || 1);
      const art = await buscarArticuloPorClave(empresaId, clave);
      setItems((arr) =>
        arr.map((raw, i) => {
          if (i !== idx) return raw;
          const it = ensureItem(raw);
          return {
            ...it,
            descripcion: art?.descripcion ?? it.descripcion,
            unidadMedida: art?.unidadMedida ?? it.unidadMedida,
            claveUnidad: art?.claveUnidad ?? it.claveUnidad,
            codSat: art?.codSat ?? it.codSat,
            precioCompra: Number.isFinite(
              Number(art?.precioCompra ?? art?.precio),
            )
              ? Number(art?.precioCompra ?? art?.precio)
              : it.precioCompra,
          };
        }),
      );
      setRowMsg(idx, "");
    } catch {
      setItems((arr) =>
        arr.map((raw, i) =>
          i === idx ? { ...ensureItem(raw), descripcion: "" } : raw,
        ),
      );
      setRowMsg(idx, "Articulo no encontrado.");
    }
  };

  const abrirBuscadorSyscom = (idx) => {
    setSyscomModalIdx(idx);
    const it = ensureItem(items[idx]);
    setSyscomQuery(it.descripcion || it.articulo || "");
    setSyscomResultados([]);
    setSyscomError("");
    setSyscomBuscado(false);
  };

  const cerrarBuscadorSyscom = () => {
    setSyscomModalIdx(null);
    setSyscomQuery("");
    setSyscomResultados([]);
    setSyscomError("");
    setSyscomBuscado(false);
  };

  const ejecutarBusquedaSyscom = async () => {
    const q = syscomQuery.trim();
    if (!q) return;
    setSyscomLoading(true);
    setSyscomError("");
    try {
      const { productos } = await buscarProductosSyscom({ busqueda: q, limit: 20 });
      setSyscomResultados(productos);
    } catch (err) {
      setSyscomResultados([]);
      setSyscomError(
        err?.response?.data?.message || err?.message || "No se pudo consultar el catálogo de Syscom."
      );
    } finally {
      setSyscomLoading(false);
      setSyscomBuscado(true);
    }
  };

  const elegirProductoSyscom = (producto) => {
    const idx = syscomModalIdx;
    if (idx == null) return;
    setItems((arr) =>
      arr.map((raw, i) => {
        if (i !== idx) return raw;
        const it = ensureItem(raw);
        return {
          ...it,
          articulo: producto.clave || it.articulo,
          descripcion: producto.descripcion || it.descripcion,
          unidadMedida: producto.unidadMedida || it.unidadMedida || "PZA",
          claveUnidad: producto.claveUnidad || it.claveUnidad || "",
          codSat: producto.codSat || it.codSat || "",
          precioCompra: producto.precioCompra || it.precioCompra || 0,
        };
      }),
    );
    setRowMsg(idx, "");
    cerrarBuscadorSyscom();
  };

  /* Calculos */
  const subtotalAntesGlobal = useMemo(() => {
    let s = 0;
    for (const raw of items.filter(Boolean)) {
      const it = ensureItem(raw);
      s += calcImportes({
        precioBase: it.precioCompra,
        aum: it.porcIncremento,
        desc: it.porcDescto,
        qty: it.cantidad,
      }).impDesc;
    }
    return round2(s);
  }, [items]);

  const subtotal = useMemo(() => {
    let s = subtotalAntesGlobal;
    if (Number(form.porcDsctoGlobal) > 0)
      s *= 1 - Number(form.porcDsctoGlobal) / 100;
    if (Number(form.incrementoGlobal) > 0)
      s *= 1 + Number(form.incrementoGlobal) / 100;
    return round2(s);
  }, [subtotalAntesGlobal, form.porcDsctoGlobal, form.incrementoGlobal]);

  const iva = useMemo(() => round2(subtotal * 0.16), [subtotal]);
  const total = useMemo(() => round2(subtotal + iva), [subtotal, iva]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.cliente?.trim()) { toastWarning("Captura la clave del cliente."); return; }
    if (!form.nombreCliente?.trim()) { toastWarning("El cliente no existe."); return; }
    const detalles = items
      .filter(Boolean)
      .map(ensureItem)
      .filter((it) => it.articulo || it.descripcion);
    if (!detalles.length) { toastWarning("Agrega al menos un renglon."); return; }
    try {
      setSaving(true);
      await crearCotizacionCompleta({
        cliente: form.cliente,
        nombreCliente: form.nombreCliente || form.cliente,
        fecha: form.fecha,
        moneda: form.moneda,
        tipoCambio: form.moneda === "D" ? Number(form.tipoCambio || 0) : 0,
        observaciones: form.observaciones,
        notasAdicionales: form.notasAdicionales,
        porcDsctoGlobal: Number(form.porcDsctoGlobal || 0),
        incrementoGlobal: Number(form.incrementoGlobal || 0),
        detalles: detalles.map((it) => ({
          articulo: it.articulo,
          descripcion: it.descripcion || "",
          unidadMedida: it.unidadMedida || "PZA",
          claveUnidad: it.claveUnidad || "",
          cantidad: Number(it.cantidad ?? 0),
          precioUnitario: Number(it.precioCompra ?? 0),
          porcDescto: Number(it.porcDescto ?? 0),
          porcIncremento: Number(it.porcIncremento ?? 0),
          codigoFabricante: it.codigoFabricante || null,
          codSat: it.codSat || null,
        })),
      });
      navigate("/cotizaciones");
    } catch (e2) {
      toastError(e2?.response?.data?.detail || e2.message || "No se pudo crear la cotizacion");
    } finally {
      setSaving(false);
    }
  };

  /* ── Estilos de celda de tabla ── */
  const cellStyle = {
    padding: "8px 10px",
    borderBottom: "1px solid var(--clr-border-subtle)",
    verticalAlign: "middle",
  };
  const cellNum = { ...cellStyle, textAlign: "right" };

  return (
    <div style={{ maxWidth: 1340 }}>
      <Toast toasts={toasts} onClose={closeToast} />
      {/* Page Header */}
      <div className="lmd-page-header">
        <div className="lmd-page-header-row">
          <div>
            <h1 className="lmd-page-title">Nueva Cotizacion</h1>
            <p className="lmd-page-subtitle">
              Completa los datos del encabezado y agrega los renglones
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={onSubmit} style={{ display: "grid", gap: "var(--sp-4)" }}>
        {/* ── ENCABEZADO ── */}
        <div className="lmd-card">
          <div className="lmd-card-header">
            <h3 className="lmd-card-title">Datos del encabezado</h3>
          </div>
          <div
            className="lmd-card-body"
            style={{ display: "grid", gap: "var(--sp-4)" }}
          >
            {/* Fila 1: Cliente */}
            <div className="lmd-grid lmd-grid-1-2">
              <div className="lmd-field">
                <label className="lmd-label">Cliente *</label>
                <input
                  className="lmd-input"
                  name="cliente"
                  value={form.cliente}
                  onChange={onChangeHeader}
                  onBlur={onClienteBlur}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      onClienteBlur();
                    }
                  }}
                  placeholder="Clave del cliente (ej. HUIZACHE)"
                />
                {clienteLoading && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      marginTop: 4,
                      fontSize: "var(--fs-xs)",
                      color: "var(--clr-text-muted)",
                    }}
                  >
                    <div
                      className="lmd-spinner"
                      style={{ width: 12, height: 12 }}
                    />{" "}
                    Buscando...
                  </div>
                )}
                {clienteMsg && (
                  <p
                    style={{
                      margin: "4px 0 0",
                      fontSize: "var(--fs-xs)",
                      color: "var(--clr-danger)",
                    }}
                  >
                    {clienteMsg}
                  </p>
                )}
              </div>
              <div className="lmd-field">
                <label className="lmd-label">Nombre del cliente</label>
                <input
                  className="lmd-input"
                  name="nombreCliente"
                  value={form.nombreCliente}
                  onChange={onChangeHeader}
                  placeholder="Se llena automaticamente al capturar la clave"
                  style={{
                    background: form.nombreCliente ? "#fff" : "var(--clr-bg)",
                  }}
                />
              </div>
            </div>

            {/* Fila 2: Fecha / Moneda / Tipo cambio */}
            <div className={`lmd-grid ${form.moneda === "D" ? "lmd-grid-3" : "lmd-grid-2"}`}>
              <div className="lmd-field">
                <label className="lmd-label">Fecha</label>
                <input
                  className="lmd-input"
                  type="date"
                  name="fecha"
                  value={form.fecha}
                  onChange={onChangeHeader}
                />
              </div>
              <div className="lmd-field">
                <label className="lmd-label">Moneda</label>
                <select
                  className="lmd-select"
                  name="moneda"
                  value={form.moneda}
                  onChange={onChangeHeader}
                  style={{ width: "100%" }}
                >
                  <option value="P">MXN — Pesos</option>
                  <option value="D">USD — Dolares</option>
                </select>
              </div>
              {form.moneda === "D" && (
                <div className="lmd-field">
                  <label className="lmd-label">Tipo de cambio</label>
                  <input
                    className="lmd-input"
                    type="number"
                    name="tipoCambio"
                    value={form.tipoCambio}
                    step="0.0001"
                    onChange={onChangeHeader}
                  />
                </div>
              )}
            </div>

            {/* Fila 3: Observaciones / Notas */}
            <div className="lmd-grid lmd-grid-2">
              <div className="lmd-field">
                <label className="lmd-label">Observaciones</label>
                <input
                  className="lmd-input"
                  name="observaciones"
                  value={form.observaciones}
                  onChange={onChangeHeader}
                />
              </div>
              <div className="lmd-field">
                <label className="lmd-label">Notas adicionales</label>
                <input
                  className="lmd-input"
                  name="notasAdicionales"
                  value={form.notasAdicionales}
                  onChange={onChangeHeader}
                />
              </div>
            </div>

            {/* Fila 4: Descuento global / Incremento global */}
            <div className="lmd-grid lmd-grid-1-1-2">
              <div className="lmd-field">
                <label className="lmd-label">% Descuento global</label>
                <input
                  className="lmd-input"
                  type="number"
                  name="porcDsctoGlobal"
                  value={form.porcDsctoGlobal}
                  step="0.01"
                  onChange={onChangeHeader}
                />
              </div>
              <div className="lmd-field">
                <label className="lmd-label">% Incremento global</label>
                <input
                  className="lmd-input"
                  type="number"
                  name="incrementoGlobal"
                  value={form.incrementoGlobal}
                  step="0.01"
                  onChange={onChangeHeader}
                />
              </div>
              <div /> {/* spacer */}
            </div>
          </div>
        </div>

        {/* ── DETALLE ── */}
        <div className="lmd-card">
          <div className="lmd-card-header">
            <h3 className="lmd-card-title">Detalle de renglones</h3>
            <button
              type="button"
              className="lmd-btn lmd-btn-primary"
              onClick={addRow}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
              >
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Agregar renglon
            </button>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                tableLayout: "fixed",
              }}
            >
              <colgroup>
                <col style={{ width: "9%" }} /> {/* Articulo */}
                <col /> {/* Descripcion */}
                <col style={{ width: "6%" }} /> {/* UM */}
                <col style={{ width: "8%" }} /> {/* Clave unidad */}
                <col style={{ width: "9%" }} /> {/* Cod SAT */}
                <col style={{ width: "6%" }} /> {/* Cant */}
                <col style={{ width: "8%" }} /> {/* Precio */}
                <col style={{ width: "6%" }} /> {/* Desc */}
                <col style={{ width: "6%" }} /> {/* Aum */}
                <col style={{ width: "8%" }} /> {/* P. venta */}
                <col style={{ width: "9%" }} /> {/* Importe */}
                <col style={{ width: "5%" }} /> {/* Quitar */}
              </colgroup>

              <thead>
                <tr
                  style={{
                    background: "var(--clr-bg)",
                    borderBottom: "2px solid var(--clr-border)",
                  }}
                >
                  {[
                    "Articulo",
                    "Descripcion",
                    "UM",
                    "Clave unidad",
                    "Cod. SAT",
                    "Cant.",
                    "Precio",
                    "% Desc",
                    "% Aum",
                    "P. venta",
                    "Importe",
                    "",
                  ].map((h, i) => (
                    <th
                      key={i}
                      style={{
                        padding: "9px 10px",
                        fontSize: "var(--fs-xs)",
                        fontWeight: "var(--fw-semibold)",
                        color: "var(--clr-text-secondary)",
                        textTransform: "uppercase",
                        letterSpacing: ".4px",
                        textAlign: i >= 5 && i <= 10 ? "right" : "left",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {items.filter(Boolean).map((raw, idx) => {
                  const it = ensureItem(raw);
                  const { pVenta, impDesc } = calcImportes({
                    precioBase: it.precioCompra,
                    aum: it.porcIncremento,
                    desc: it.porcDescto,
                    qty: it.cantidad,
                  });
                  const isEven = idx % 2 === 0;

                  return (
                    <tr
                      key={idx}
                      style={{ background: isEven ? "#fff" : "#fafbfc" }}
                    >
                      <td style={cellStyle}>
                        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          <CellInput
                            value={it.articulo}
                            placeholder="Clave"
                            onChange={(e) =>
                              onChangeItem(idx, "articulo", e.target.value)
                            }
                            onBlur={() => lookupArticulo(idx)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                lookupArticulo(idx);
                              }
                            }}
                          />
                          <button
                            type="button"
                            title="Buscar en el catálogo de Syscom"
                            onClick={() => abrirBuscadorSyscom(idx)}
                            style={{
                              flexShrink: 0,
                              width: 22,
                              height: 22,
                              border: "1px solid var(--clr-border)",
                              borderRadius: "var(--radius-sm)",
                              background: "#fff",
                              color: "var(--clr-text-muted)",
                              cursor: "pointer",
                              fontSize: 12,
                              lineHeight: 1,
                            }}
                          >
                            🔍
                          </button>
                        </div>
                        {rowMsgs[idx] && (
                          <p
                            style={{
                              margin: "3px 0 0",
                              fontSize: 11,
                              color: rowMsgs[idx].includes("no")
                                ? "var(--clr-danger)"
                                : "var(--clr-text-muted)",
                            }}
                          >
                            {rowMsgs[idx]}
                          </p>
                        )}
                      </td>
                      <td style={cellStyle}>
                        <CellInput
                          value={it.descripcion}
                          placeholder="Descripcion"
                          onChange={(e) =>
                            onChangeItem(idx, "descripcion", e.target.value)
                          }
                        />
                      </td>
                      <td style={cellStyle}>
                        <CellInput
                          value={it.unidadMedida}
                          placeholder="UM"
                          onChange={(e) =>
                            onChangeItem(idx, "unidadMedida", e.target.value)
                          }
                        />
                      </td>
                      <td style={cellStyle}>
                        <CellInput
                          value={it.claveUnidad}
                          placeholder="E48"
                          onChange={(e) =>
                            onChangeItem(idx, "claveUnidad", e.target.value)
                          }
                        />
                      </td>
                      <td style={cellStyle}>
                        <CellInput
                          value={it.codSat}
                          placeholder="81111508"
                          onChange={(e) =>
                            onChangeItem(idx, "codSat", e.target.value)
                          }
                        />
                      </td>
                      <td style={cellNum}>
                        <CellInput
                          value={it.cantidad}
                          type="number"
                          align="right"
                          onChange={(e) =>
                            onChangeItem(idx, "cantidad", e.target.value)
                          }
                        />
                      </td>
                      <td style={cellNum}>
                        <CellInput
                          value={it.precioCompra}
                          type="number"
                          align="right"
                          onChange={(e) =>
                            onChangeItem(idx, "precioCompra", e.target.value)
                          }
                        />
                      </td>
                      <td style={cellNum}>
                        <CellInput
                          value={it.porcDescto}
                          type="number"
                          align="right"
                          onChange={(e) =>
                            onChangeItem(idx, "porcDescto", e.target.value)
                          }
                        />
                      </td>
                      <td style={cellNum}>
                        <CellInput
                          value={it.porcIncremento}
                          type="number"
                          align="right"
                          onChange={(e) =>
                            onChangeItem(idx, "porcIncremento", e.target.value)
                          }
                        />
                      </td>
                      <td
                        style={{
                          ...cellNum,
                          fontVariantNumeric: "tabular-nums",
                          fontFamily: "var(--font-mono)",
                          fontSize: "var(--fs-xs)",
                          color: "var(--clr-text-secondary)",
                        }}
                      >
                        {fmtMX(pVenta)}
                      </td>
                      <td
                        style={{
                          ...cellNum,
                          fontVariantNumeric: "tabular-nums",
                          fontFamily: "var(--font-mono)",
                          fontSize: "var(--fs-sm)",
                          fontWeight: "var(--fw-semibold)",
                          color: "var(--clr-text-primary)",
                        }}
                      >
                        {fmtMX(impDesc)}
                      </td>
                      <td style={{ ...cellStyle, textAlign: "center" }}>
                        <button
                          type="button"
                          onClick={() => rmRow(idx)}
                          style={{
                            border: "none",
                            background: "none",
                            cursor: "pointer",
                            color: "var(--clr-danger)",
                            padding: 4,
                            borderRadius: "var(--radius-sm)",
                            fontSize: 16,
                            lineHeight: 1,
                          }}
                          title="Quitar renglon"
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── TOTALES ── */}
        <div className="lmd-card">
          <div className="lmd-card-body">
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <div style={{ width: 280, display: "grid", gap: "var(--sp-2)" }}>
                {[
                  { label: "Subtotal", value: subtotal },
                  { label: "IVA 16%", value: iva },
                ].map(({ label, value }) => (
                  <div
                    key={label}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "6px 0",
                      borderBottom: "1px solid var(--clr-border-subtle)",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "var(--fs-sm)",
                        color: "var(--clr-text-secondary)",
                      }}
                    >
                      {label}
                    </span>
                    <span
                      style={{
                        fontFamily: "var(--font-mono)",
                        fontVariantNumeric: "tabular-nums",
                        fontSize: "var(--fs-sm)",
                      }}
                    >
                      ${fmtMX(value)}
                    </span>
                  </div>
                ))}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "10px 0 4px",
                  }}
                >
                  <span
                    style={{
                      fontWeight: "var(--fw-bold)",
                      fontSize: "var(--fs-md)",
                    }}
                  >
                    Total
                  </span>
                  <span
                    style={{
                      fontFamily: "var(--font-mono)",
                      fontWeight: "var(--fw-extrabold)",
                      fontSize: "var(--fs-xl)",
                      color: "var(--clr-primary)",
                    }}
                  >
                    ${fmtMX(total)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── ACCIONES ── */}
        <div
          style={{
            display: "flex",
            gap: "var(--sp-3)",
            paddingBottom: "var(--sp-8)",
          }}
        >
          <button
            type="submit"
            className="lmd-btn lmd-btn-primary"
            disabled={saving}
          >
            {saving ? (
              <>
                <div
                  className="lmd-spinner"
                  style={{ width: 14, height: 14 }}
                />{" "}
                Guardando...
              </>
            ) : (
              <>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                >
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                  <polyline points="17 21 17 13 7 13 7 21" />
                  <polyline points="7 3 7 8 15 8" />
                </svg>
                Crear cotizacion
              </>
            )}
          </button>
          <button
            type="button"
            className="lmd-btn lmd-btn-secondary"
            onClick={() => navigate("/cotizaciones")}
          >
            Cancelar
          </button>
        </div>
      </form>

      {/* Modal Buscar en Syscom */}
      {syscomModalIdx !== null && (
        <div className="lmd-modal-overlay" onClick={cerrarBuscadorSyscom}>
          <div className="lmd-modal lmd-modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="lmd-modal-header">
              <h3 className="lmd-modal-title">Buscar en catálogo Syscom</h3>
              <button className="lmd-modal-close" onClick={cerrarBuscadorSyscom}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="lmd-modal-body">
              <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                <input
                  className="lmd-input"
                  autoFocus
                  placeholder="Describe lo que buscas, ej. switch 24 puertos PoE gigabit"
                  value={syscomQuery}
                  onChange={(e) => setSyscomQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      ejecutarBusquedaSyscom();
                    }
                  }}
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  className="lmd-btn lmd-btn-primary"
                  disabled={syscomLoading || !syscomQuery.trim()}
                  onClick={ejecutarBusquedaSyscom}
                >
                  {syscomLoading ? "Buscando..." : "Buscar"}
                </button>
              </div>

              {syscomError && (
                <p style={{ color: "var(--clr-danger)", fontSize: "var(--fs-sm)", margin: "0 0 12px" }}>{syscomError}</p>
              )}

              <div style={{ maxHeight: 420, overflowY: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
                {syscomResultados.map((p) => (
                  <div
                    key={p.productoId || p.clave}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: 10,
                      border: "1px solid var(--clr-border)",
                      borderRadius: "var(--radius-sm)",
                    }}
                  >
                    {p.imagen ? (
                      <img src={p.imagen} alt="" width={48} height={48} style={{ objectFit: "contain", flexShrink: 0 }} />
                    ) : (
                      <div style={{ width: 48, height: 48, flexShrink: 0, background: "#f3f4f6", borderRadius: 4 }} />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: "var(--fs-sm)", fontWeight: 600 }}>{p.marca} — {p.clave}</div>
                      <div style={{ fontSize: 12, color: "var(--clr-text-secondary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {p.descripcion}
                      </div>
                      <div style={{ fontSize: 11, color: p.existencia > 0 ? "var(--clr-success)" : "var(--clr-danger)" }}>
                        {p.existencia > 0 ? `${p.existencia} en existencia` : "Sin existencia"}
                      </div>
                    </div>
                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      <div style={{ fontWeight: 700 }}>${fmtMX(p.precioCompra)}</div>
                      <button
                        type="button"
                        className="lmd-btn lmd-btn-primary lmd-btn-sm"
                        style={{ marginTop: 4 }}
                        onClick={() => elegirProductoSyscom(p)}
                      >
                        Usar
                      </button>
                    </div>
                  </div>
                ))}

                {syscomBuscado && !syscomLoading && syscomResultados.length === 0 && !syscomError && (
                  <p style={{ color: "var(--clr-text-secondary)", fontSize: "var(--fs-sm)" }}>
                    Sin resultados para "{syscomQuery}".
                  </p>
                )}
              </div>
            </div>
            <div className="lmd-modal-footer">
              <button className="lmd-btn lmd-btn-secondary" onClick={cerrarBuscadorSyscom}>Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
